import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  API_BASE_URL,
  NETCASH_SERVICE_KEY,
  NETCASH_VENDOR_KEY,
} from '../../../config/config';
import { PaymentClient, CampaignClient } from '../../../services/apiClient';
import axiosInstance from '../../../services/axiosInstance';
import Loader from '../../../components/common/Loader';
import SimpleAlert from '../../../components/common/SimpleAlert';

const DonationRedirect = () => {
  const { paymentId } = useParams();
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const paymentClient = new PaymentClient(API_BASE_URL, axiosInstance);
  const campaignClient = new CampaignClient(API_BASE_URL, axiosInstance);

  const calculateSubscriptionStartDate = (debitDay: number): string => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed
    const selectedDay = Math.min(debitDay, 28); // Prevent invalid dates like Feb 31

    const candidateDate = new Date(currentYear, currentMonth, selectedDay);

    // If the candidate date is today or in the future, use it. Otherwise, use next month.
    if (candidateDate >= today) {
      return candidateDate.toISOString().split('T')[0];
    }

    const nextMonthDate = new Date(currentYear, currentMonth + 1, selectedDay);
    return nextMonthDate.toISOString().split('T')[0];
  };

  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      if (!paymentId || hasSubmittedRef.current) return;
      hasSubmittedRef.current = true;

      try {
        const paymentRes = await paymentClient.getPaymentById(paymentId);
        const payment = paymentRes?.data;

        if (!payment || !payment.campaignId) return;

        const imageRes = await campaignClient.generateCampaignImageSas(
          payment.campaignId,
        );
        if (imageRes?.success) setBackgroundImage(imageRes.data ?? '');

        const form = formRef.current;
        if (!form) return;

        const isSubscription = payment.subscription === true;
        const debitDay = payment.debitDay ?? 1;

        const fields: Record<string, any> = {
          m1: NETCASH_SERVICE_KEY,
          p2: payment.id,
          p3: `${payment.memberName?.toUpperCase() ?? ''}: (ref: ${payment.id})`,
          p4: payment.amount,
          Budget: 'Y',
          m9: payment.memberEmail,
          m11: '',
          m14: isSubscription ? 1 : 0,
          m16: isSubscription ? 1 : 0,
          m17: 999,
          m18: 1,
          m19: isSubscription ? calculateSubscriptionStartDate(debitDay) : '',
          m20: isSubscription ? payment.amount : '',
        };

        Object.entries(fields).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });

        setTimeout(() => form.submit(), 2000);
      } catch (err) {
        console.error('Redirect failed:', err);
      }
    };

    fetchAndRedirect();
  }, [paymentId]);

  return (
    <div
      className="w-full h-screen flex items-center justify-center relative"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50 z-0" />
      <div className="z-10 bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-center">
        <img src="/images/logo.png" className="mx-auto mb-6 w-40" />
        <div className="text-gray-700 text-lg font-semibold">
          Redirecting to payment provider...
        </div>
        <SimpleAlert
          message="You are now being redirected to our secure payment provider (Netcash). Please do not close this window."
          variant="info"
        />
        <div className="mt-4">
          <Loader />
        </div>
        <form
          ref={formRef}
          method="POST"
          action="https://paynow.netcash.co.za/site/paynow.aspx"
          target="_top"
          className="hidden"
        ></form>
      </div>
    </div>
  );
};

export default DonationRedirect;
